import type { VesselDrawState } from './types';

/**
 * Large motorboat: a sport motor yacht after the Riviera 5600 — long low hull
 * with a level sheer, navy topside, thin white boot, long forward-raked bow with
 * a white stem and fine entry, sculpted black hull window, low coupe hardtop and
 * a tall mast. Bow right.
 */
export const drawMotorLarge = ({ palette, night }: VesselDrawState): void => {
  const { ctx } = palette;
  const c = (hex: string): string => palette.colour(hex);

  // swim platform (stern)
  ctx.fillStyle = c('E7EAED');
  ctx.beginPath();
  ctx.moveTo(-56, -1.4);
  ctx.lineTo(-68, -1.4);
  ctx.quadraticCurveTo(-69, -1.4, -69, 0.2);
  ctx.lineTo(-69, 1.5);
  ctx.lineTo(-56, 1.5);
  ctx.closePath();
  ctx.fill();
  ctx.fillStyle = c('C8A06E');
  ctx.fillRect(-67, -0.6, 11, 1);

  // hull (navy) — level sheer, long forward-raked bow
  ctx.beginPath();
  ctx.moveTo(-58, -9);
  ctx.quadraticCurveTo(-49, -12.5, -30, -12.5);
  ctx.quadraticCurveTo(12, -12.5, 54, -12.5); // level deck line
  ctx.quadraticCurveTo(66, -12.4, 71, -12); // level to the stemhead
  ctx.quadraticCurveTo(73, -11.5, 72, -9); // round the stemhead (forwardmost)
  ctx.quadraticCurveTo(71, -5, 67, -2); // stem slopes down-and-back (forward rake)
  ctx.quadraticCurveTo(63, 0, 58, 1.5); // fine forefoot
  ctx.quadraticCurveTo(48, 3.5, 32, 4);
  ctx.quadraticCurveTo(-6, 6, -52, 4);
  ctx.lineTo(-58, 1);
  ctx.closePath();
  const navy = ctx.createLinearGradient(0, -13, 0, 2);
  navy.addColorStop(0, c('33426A'));
  navy.addColorStop(0.5, c('22304E'));
  navy.addColorStop(1, c('171F33'));
  ctx.fillStyle = navy;
  ctx.fill();
  ctx.lineWidth = 1;
  ctx.strokeStyle = c('11151F');
  ctx.stroke();

  // white bottom + white stem (boot sweeps up at the bow)
  ctx.beginPath();
  ctx.moveTo(-56, -2.4);
  ctx.quadraticCurveTo(-6, -1.5, 40, -2.6);
  ctx.quadraticCurveTo(56, -4, 66, -6);
  ctx.quadraticCurveTo(70, -7.5, 72, -9.4); // up the stem
  ctx.lineTo(72, -9);
  ctx.quadraticCurveTo(71, -5, 67, -2);
  ctx.quadraticCurveTo(63, 0, 58, 1.5);
  ctx.quadraticCurveTo(48, 3.5, 32, 4);
  ctx.quadraticCurveTo(-6, 6, -52, 4);
  ctx.lineTo(-56, 1);
  ctx.closePath();
  const boot = ctx.createLinearGradient(0, -13, 0, 4);
  boot.addColorStop(0, c('F4F7F9'));
  boot.addColorStop(0.55, c('E8EDF0'));
  boot.addColorStop(1, c('D5DADF'));
  ctx.fillStyle = boot;
  ctx.fill();

  // chine / boot divide line sweeping up to the bow
  ctx.beginPath();
  ctx.moveTo(-56, -2.4);
  ctx.quadraticCurveTo(-6, -1.5, 40, -2.6);
  ctx.quadraticCurveTo(56, -4, 66, -6);
  ctx.quadraticCurveTo(70, -7.5, 72, -9.4);
  ctx.strokeStyle = c('AEB6C0');
  ctx.lineWidth = 0.5;
  ctx.globalAlpha = 0.5;
  ctx.stroke();
  ctx.globalAlpha = 1;

  // sculpted black hull window (long, with a hooked tail aft)
  ctx.beginPath();
  ctx.moveTo(-26, -6.2);
  ctx.quadraticCurveTo(-22, -7.4, -16, -7.2);
  ctx.quadraticCurveTo(6, -7.6, 26, -7);
  ctx.quadraticCurveTo(34, -6.6, 37, -5.6);
  ctx.quadraticCurveTo(34, -4.2, 27, -4);
  ctx.quadraticCurveTo(4, -3.2, -12, -4.4);
  ctx.quadraticCurveTo(-20, -5, -24, -6);
  ctx.quadraticCurveTo(-27, -6.6, -26, -6.2);
  ctx.closePath();
  ctx.fillStyle = c('090D15');
  ctx.fill();
  ctx.strokeStyle = c('A6B0BB');
  ctx.lineWidth = 0.4;
  ctx.globalAlpha = 0.4;
  ctx.stroke();
  ctx.globalAlpha = 1;
  ctx.strokeStyle = c('566A84');
  ctx.lineWidth = 0.6;
  ctx.globalAlpha = 0.5;
  ctx.beginPath();
  ctx.moveTo(-16, -6.4);
  ctx.quadraticCurveTo(6, -6.8, 28, -6);
  ctx.stroke();
  ctx.globalAlpha = 1;

  // bright sheer stripe + white cap rail (level)
  ctx.beginPath();
  ctx.moveTo(-56, -10.8);
  ctx.quadraticCurveTo(-10, -11.4, 30, -11.4);
  ctx.quadraticCurveTo(50, -11.4, 68, -11.2);
  ctx.strokeStyle = c('EAEFF3');
  ctx.lineWidth = 1.4;
  ctx.stroke();
  ctx.beginPath();
  ctx.moveTo(-58, -9);
  ctx.quadraticCurveTo(-49, -12.5, -30, -12.5);
  ctx.quadraticCurveTo(12, -12.5, 54, -12.5);
  ctx.quadraticCurveTo(66, -12.4, 71, -12);
  ctx.strokeStyle = c('FFFFFF');
  ctx.globalAlpha = 0.85;
  ctx.lineWidth = 1;
  ctx.stroke();
  ctx.globalAlpha = 1;

  // forward hull-side boarding gate + small portlights
  ctx.strokeStyle = c('5A6A86');
  ctx.lineWidth = 0.4;
  ctx.globalAlpha = 0.6;
  ctx.strokeRect(-34, -11, 5, 6.4);
  ctx.globalAlpha = 1;
  ctx.fillStyle = c('090D15');
  [44, 49].forEach((px) => {
    ctx.beginPath();
    ctx.ellipse(px, -9.6, 1.4, 1, 0, 0, Math.PI * 2);
    ctx.fill();
  });

  // superstructure (low white coupe)
  ctx.beginPath();
  ctx.moveTo(-32, -13);
  ctx.lineTo(-32, -22);
  ctx.quadraticCurveTo(-32, -27, -25, -28);
  ctx.quadraticCurveTo(2, -29, 26, -27);
  ctx.quadraticCurveTo(35, -26, 39, -21);
  ctx.lineTo(45, -13);
  ctx.quadraticCurveTo(20, -12.5, -32, -13);
  ctx.closePath();
  const superstructure = ctx.createLinearGradient(0, -29, 0, -13);
  superstructure.addColorStop(0, c('F6F8FA'));
  superstructure.addColorStop(1, c('DCE1E6'));
  ctx.fillStyle = superstructure;
  ctx.fill();
  ctx.strokeStyle = c('AEB7C0');
  ctx.lineWidth = 0.7;
  ctx.stroke();

  // long side glass
  ctx.beginPath();
  ctx.moveTo(-27, -15);
  ctx.lineTo(-27, -24);
  ctx.quadraticCurveTo(-27, -26, -24, -26);
  ctx.lineTo(22, -26.5);
  ctx.quadraticCurveTo(29, -26, 32, -22);
  ctx.lineTo(35, -15);
  ctx.quadraticCurveTo(34, -14, 30, -14.5);
  ctx.closePath();
  ctx.fillStyle = c('212B3A');
  ctx.globalAlpha = 0.94;
  ctx.fill();
  ctx.globalAlpha = 1;

  // raked windscreen, flush between the side glass and the bow-front edge
  ctx.beginPath();
  ctx.moveTo(32, -22);
  ctx.lineTo(39, -21);
  ctx.lineTo(44, -14);
  ctx.lineTo(35, -15);
  ctx.closePath();
  ctx.fillStyle = c('27313F');
  ctx.fill();

  // mullions
  ctx.strokeStyle = c('C9CFD8');
  ctx.lineWidth = 0.5;
  ctx.globalAlpha = 0.5;
  ctx.beginPath();
  ctx.moveTo(-10, -16);
  ctx.lineTo(-9, -26);
  ctx.moveTo(6, -16.5);
  ctx.lineTo(7, -26.5);
  ctx.moveTo(20, -16.5);
  ctx.lineTo(21, -26.5);
  ctx.stroke();
  ctx.globalAlpha = 1;

  // roof highlight
  ctx.strokeStyle = c('FFFFFF');
  ctx.globalAlpha = 0.6;
  ctx.lineWidth = 1;
  ctx.beginPath();
  ctx.moveTo(-25, -28);
  ctx.quadraticCurveTo(2, -29, 26, -27);
  ctx.quadraticCurveTo(35, -26, 39, -21);
  ctx.stroke();
  ctx.globalAlpha = 1;

  // tall thin mast: base pod, radar dome, single antenna
  ctx.fillStyle = c('282E36');
  ctx.beginPath();
  ctx.moveTo(-7, -27);
  ctx.lineTo(3, -27);
  ctx.lineTo(2, -31);
  ctx.lineTo(-6, -31);
  ctx.closePath();
  ctx.fill();
  ctx.fillStyle = c('3A4049');
  ctx.beginPath();
  ctx.ellipse(-2.5, -32.4, 3.2, 2, 0, 0, Math.PI * 2);
  ctx.fill();
  ctx.fillStyle = c('20262C');
  ctx.fillRect(-5.7, -32.4, 6.4, 1.2);
  ctx.strokeStyle = c('3A434B');
  ctx.lineWidth = 0.8;
  ctx.beginPath();
  ctx.moveTo(-2.5, -31);
  ctx.lineTo(-4.5, -46);
  ctx.stroke();

  // stainless anchor fitting at the stemhead
  ctx.fillStyle = c('C2CAD2');
  ctx.beginPath();
  ctx.moveTo(71.5, -12);
  ctx.lineTo(75.5, -11.5);
  ctx.lineTo(75.5, -10.4);
  ctx.lineTo(71.5, -10.7);
  ctx.closePath();
  ctx.fill();

  // bow pulpit rail (level, following the bow to the stemhead)
  ctx.strokeStyle = c('D2DAE0');
  ctx.lineWidth = 0.8;
  ctx.globalAlpha = 0.85;
  ctx.beginPath();
  ctx.moveTo(40, -13);
  ctx.quadraticCurveTo(56, -14, 71, -14);
  ctx.moveTo(48, -12.6);
  ctx.lineTo(49, -13.9);
  ctx.moveTo(58, -12.7);
  ctx.lineTo(59, -14);
  ctx.stroke();
  ctx.globalAlpha = 1;

  // transom boarding ladder (stern)
  ctx.strokeStyle = c('C9D2DA');
  ctx.lineWidth = 0.7;
  ctx.beginPath();
  ctx.moveTo(-62, -4);
  ctx.lineTo(-62, -1);
  ctx.moveTo(-59, -4);
  ctx.lineTo(-59, -1);
  ctx.moveTo(-62, -3);
  ctx.lineTo(-59, -3);
  ctx.moveTo(-62, -2);
  ctx.lineTo(-59, -2);
  ctx.stroke();

  if (night > 0.04) {
    const alpha = Math.min(0.95, night * 1.05);
    palette.navGlow(71, -10.5, 8, '45,210,95', alpha);
    palette.navGlow(-57, -8, 7, '255,250,235', alpha * 0.9);
    palette.navGlow(-4.5, -46, 9, '255,250,235', alpha);
    ctx.fillStyle = `rgba(255,200,130,${0.5 * night})`;
    ctx.beginPath();
    ctx.ellipse(2, -20, 11, 4, 0, 0, Math.PI * 2);
    ctx.fill();
  }
};
